import React, { useEffect, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import FormStepper from 'components/base/form-stepper';
import { DefaultTransferDetails } from 'utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddInternalProcurementType } from 'models/procurement.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { Input, ToastMessage } from '@cokitchen/cokitchen-components';

import NewTransferForm from './new-transfer-form';
import NewTransferPreview from './new-transfer-preview';
import SelectDropdown from 'components/base/select-dropdown';
import { StoreType } from 'models/store.model';
import useTransferContext from 'contexts/transfer.context/context';
import {
  buildTransferRoute,
  getTransferStoreTypeFromPathname,
  normalizeTransferStoreType,
  TRANSFER_STORE_TYPE_OPTIONS
} from '../store-type';

const initialProcurement = {
  ingredient_id: '',
  ingredient_name: '',
  unit_type: '',
  quantity: ''
};

const NewTransferRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const { setIsBackLink, setBackLinkTitle, cokitchens } = useDashboardContext();
  const [formData, setFormData] = useState<AddInternalProcurementType[]>([initialProcurement]);
  const [isCreatingProcurementRequest, setIsCreatingProcurementRequest] = useState(false);
  const router = useNavigate();
  const { activeStore } = useDashboardContext();

  const { transferDetails, setTransferDetails } = useTransferContext();
  const selectedStoreType = normalizeTransferStoreType(
    getTransferStoreTypeFromPathname(location.pathname)
  );
  const selectedStoreTypeOption = TRANSFER_STORE_TYPE_OPTIONS[selectedStoreType];
  const isStoreTypeLocked = true;
  const storeTypeOptions = [selectedStoreTypeOption];
  const transferListPath = buildTransferRoute(selectedStoreType);

  const [stores, setStores] = useState([] as StoreType[]);

  useEffect(() => {
    if (!isStoreTypeLocked || transferDetails.storeType.id === selectedStoreTypeOption.id) return;
    setTransferDetails({ ...transferDetails, storeType: selectedStoreTypeOption });
  }, [isStoreTypeLocked, selectedStoreTypeOption, transferDetails, setTransferDetails]);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle(`Return to ${selectedStoreTypeOption.name.toLowerCase()} transfer`);
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
      setFormData([initialProcurement]);
      setTransferDetails(DefaultTransferDetails);
    };
  }, [selectedStoreTypeOption.name, setBackLinkTitle, setIsBackLink, setTransferDetails]);

  const setValue = (
    index: number,
    field: keyof AddInternalProcurementType,
    value: string | number
  ) => {
    setFormData((prev) =>
      prev.map((data, i) => (i === index ? { ...data, [field]: value } : data))
    );
  };

  const addForm = () => setFormData([...formData, { ...initialProcurement }]);

  const removeItem = (index: number) => setFormData(formData.filter((data, i) => i !== index));

  const data = {
    from_store_id: transferDetails.store.id,
    transfer_type: transferDetails.storeType.id,
    to_store_id: activeStore?.id,
    title: transferDetails.title,
    note: transferDetails.note,
    items: formData.map((data) => {
      return { ingredient_id: data.ingredient_id, quantity: Number(data.quantity) };
    })
  };

  const createProcurementRequest = async () => {
    if (!activeStore?.id) return;
    try {
      setIsCreatingProcurementRequest(true);
      const response = await apiService.post(`main-store-transfer`, {
        version: 'v2',
        data
      });
      if (response) {
        router(transferListPath);
        toast(<ToastMessage message='Transfer request created successfully' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error creating procurement request'}
          type='error'
        />
      );
    } finally {
      setIsCreatingProcurementRequest(false);
    }
  };
  useEffect(() => {
    getStores();
  }, [transferDetails.cokitchen]);

  const getStores = async () => {
    if (!transferDetails.cokitchen?.id) return;
    try {
      const res = await apiService.get(`stores/cokitchen/${transferDetails.cokitchen.id}/stores`);
      setStores(res.data);
    } catch (e) {
      console.log(e);
    }
  };

  const handleEdit = () => setStep(1);
  return (
    <div className='ml-2 md:ml-8'>
      <h4 className='text-[24px] font-semibold text-[#2E3031]'>Create Transfer Request</h4>
      <p className='text-[#6F7376] text-sm mb-8'>
        Inventory running low on certain items? prepare your list{' '}
      </p>
      <div className=' w-full max-w-[678px]'>
        <FormStepper
          disabled={false}
          step={step}
          title={step === 1 ? 'Make your selections' : 'Review selections'}
          subtitle={step === 2 ? 'Ensure you have all items needed selected' : ''}
          setStep={setStep}
          handleConfirmClick={() => {
            createProcurementRequest();
          }}
          loading={isCreatingProcurementRequest}
          handleCancelClick={() => {
            navigate(-1);
          }}>
          {step === 1 ? (
            <div>
              <div className='grid grid-cols-2 gap-3 mb-3'>
                <Input
                  design='bordered'
                  label='Title'
                  name='title'
                  placeholder='Title'
                  value={transferDetails.title}
                  onChange={(e) =>
                    setTransferDetails({ ...transferDetails, title: e.target.value })
                  }
                />
                <Input
                  design='bordered'
                  label='Note'
                  name='note'
                  placeholder='note'
                  value={transferDetails.note}
                  onChange={(e) => setTransferDetails({ ...transferDetails, note: e.target.value })}
                />
                <SelectDropdown
                  label='Select Store Type '
                  placeholder='Select Store Type'
                  value={transferDetails.storeType.name || selectedStoreTypeOption.name}
                  setValue={(item) => {
                    setTransferDetails({ ...transferDetails, storeType: item });
                  }}
                  options={storeTypeOptions.map((storeTypeOption) => {
                    return { id: storeTypeOption.id, name: storeTypeOption.name };
                  })}
                />
                <SelectDropdown
                  label='Select Cokitchen'
                  placeholder='Select cokitchen'
                  value={transferDetails.cokitchen.name}
                  setValue={(item) => {
                    setTransferDetails({ ...transferDetails, cokitchen: item });
                  }}
                  options={cokitchens.map((store) => {
                    return { id: store.id, name: store.name };
                  })}
                />
                <SelectDropdown
                  label='Select Stores'
                  placeholder='Select stores'
                  value={transferDetails.store.name}
                  setValue={(item) => {
                    setTransferDetails({ ...transferDetails, store: item });
                  }}
                  options={stores
                    .filter((store) => store.id !== activeStore?.id)
                    ?.map((store) => {
                      return { id: store.id, name: store.name };
                    })}
                />
              </div>

              <p className='font-semibold mt-7 mb-4  '>Ingredients</p>

              {formData.map((data, index) => (
                <NewTransferForm
                  key={index}
                  data={data}
                  index={index}
                  setValue={setValue}
                  removeItem={removeItem}
                />
              ))}
              <button onClick={addForm} className='text-[#0080E5] mt-2 font-medium text-sm'>
                + Add Item
              </button>
            </div>
          ) : (
            <div>
              <NewTransferPreview formData={formData} handleEdit={handleEdit} />
            </div>
          )}
        </FormStepper>
      </div>
    </div>
  );
};

export default NewTransferRequest;
